import { tokyoYear, toWareki } from "../datetime.ts";
import type { SensitivityRules } from "../review-due.ts";
import { COMMON_RULES } from "./common.ts";
import type { ProfessionPack } from "./types.ts";

const yearLabel = (y: number) => `${y}年（${toWareki(y)}）`;

const STORE_SENSITIVITY: SensitivityRules = {
  amount: { label: "料金・数値", pattern: /\d[\d,，.]*\s?(?:円|%|％|名|席)|料金|価格/ },
  relief: { label: "キャンペーン・条件", pattern: /キャンペーン|クーポン|割引|特典|条件|対象/ },
  deadline: { label: "期間・期限", pattern: /期間|期限|まで|営業時間|定休日/ },
  year: { label: "時点の書き方", pattern: /令和\d+年|\d{4}年(?:時点|現在|\d+月)/ },
  reform: { label: "変更の記述", pattern: /変更|終了|新しく|リニューアル|予定/ },
};

/**
 * 店舗 (サロン・飲食店・整骨院 …): an office that tracks its marketing —
 * search, visits, the map, inquiries, 施策 — without writing articles. The
 * article side of the pack is filled in case a shop writes a blog later;
 * the screens hide it by `features.articles`.
 */
export const STORE: ProfessionPack = {
  id: "store",
  label: "店舗",
  supervisorTitle: "店長",
  registrationLabel: "",
  reviewer: "店長",
  writingRules: [
    "依頼主のお店の立場で書く。文体は「です・ます」。一人称と表記はお店の設定に従う。",
    "料金・営業時間・キャンペーンの条件は、お店の設定と資料で確認できたものだけを書く。期間のあるものは期間を明記する。",
    "効果や結果の保証（「必ず痩せる」「確実に治る」など）、根拠のない最上級、他店との比較は書かない。美容・健康・医療に関わる表現は、薬機法・医療広告ガイドライン・景品表示法に反しない。",
    "他のサイトの文章を写さない。",
    "検索した人の知りたいことに冒頭で答える。",
    "文字数稼ぎの水増しをしない。",
  ],
  expressionRules: [
    ...COMMON_RULES,
    { id: "store-health-claim", pattern: /(必ず|確実に|絶対に?)(痩せ|治|改善|効く|若返)|(治療|治る|完治|医学的に)/g, severity: "high", category: "効果の保証・医療的な表現", message: "効果や治癒を保証する表現、医療行為と受け取られる表現は、薬機法・医療広告ガイドラインに反するおそれがあります", suggestion: "「〜を目指します」「〜をサポートします」など、効果を断定しない表現にする" },
    { id: "store-comparison", pattern: /他店(より|と比べ|とは違)|他の(店|サロン|お店)(より|には(でき|ない))|地域最安/g, severity: "high", category: "他店との比較", message: "根拠のない他店との比較は使えません", suggestion: "比較をやめ、お店の特長を事実として説明する" },
  ],
  trustedDomains: ["caa.go.jp", "mhlw.go.jp", "support.google.com", "business.google.com", "laws.e-gov.go.jp", "elaws.e-gov.go.jp"],
  authorityDomains: ["hotpepper.jp", "tabelog.com", "beauty.hotpepper.jp", "epark.jp", "minimodel.jp", "ja.wikipedia.org"],
  specialtyOptions: ["美容室・ヘアサロン", "ネイル・まつげ", "エステ・リラクゼーション", "整骨院・整体", "飲食店（ランチ）", "飲食店（ディナー・宴会）", "カフェ・テイクアウト", "予約の多いメニュー", "新規のお客様の獲得", "再来店の促進", "口コミの獲得", "採用"],
  seasons: [
    { id: "shinnendo", name: "新生活（春）", start: { month: 3, day: 1 }, end: { month: 4, day: 30 }, leadWeeks: 4, terms: ["歓迎会", "新生活", "入学", "入社", "引っ越し"], note: "引っ越しや入学・入社で、新しいお店を探す人が増える時期です。" },
    { id: "golden-week", name: "大型連休", start: { month: 4, day: 25 }, end: { month: 5, day: 10 }, leadWeeks: 4, terms: ["連休", "GW", "ランチ", "家族"], note: "連休の外食と帰省で、地図からの検索が増えます。" },
    { id: "natsu", name: "夏（夏祭り・お盆）", start: { month: 7, day: 15 }, end: { month: 8, day: 31 }, leadWeeks: 4, terms: ["夏", "お盆", "帰省", "浴衣", "ビアガーデン"], note: "帰省と行事で、普段と違うお客様が来る時期です。" },
    { id: "nenmatsu", name: "年末（忘年会・年内の予約）", start: { month: 11, day: 15 }, end: { month: 12, day: 31 }, leadWeeks: 6, terms: ["忘年会", "年末", "年内", "クリスマス", "予約"], note: "忘年会と年内の身だしなみで、予約が集中する時期です。早めの告知が効きます。" },
    { id: "shinnen", name: "新年（新年会・成人式）", start: { month: 1, day: 1 }, end: { month: 1, day: 31 }, leadWeeks: 6, terms: ["新年会", "成人式", "初詣", "ヘアセット"], note: "新年会と成人式で、予約と地図の検索が増えます。" },
  ],
  officeNoun: "お店",
  officeExample: "山田ヘアサロン 本町店",
  affiliationExample: "大阪商工会議所",
  sourcesPhrase: "消費者庁や厚生労働省などの公的な資料",
  sourceExamples: "消費者庁・厚生労働省・Google ビジネスプロフィールのヘルプ",
  keyFigures: "料金・期間・数値",
  publishers: { "caa.go.jp": "消費者庁", "mhlw.go.jp": "厚生労働省", "support.google.com": "Google ヘルプ", "business.google.com": "Google ビジネスプロフィール", "e-gov.go.jp": "e-Gov 法令検索" },
  period: {
    label: yearLabel,
    short: (y) => `${y}年`,
    rules: (y) => `${yearLabel(y)}時点の情報`,
    brief: (y) => `基準とする時点：${yearLabel(y)}時点の情報（料金や期間は確認した時点を明記する）`,
    mustState: false,
    current: tokyoYear,
  },
  review: { name: "情報の見直し", when: "料金やメニュー、営業時間の変更のあと", nowLabel: "現在", currentArticle: "最新の情報に合った記事", updateLabel: "情報の更新", sensitivity: STORE_SENSITIVITY },
  features: { articles: false },
};
